import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, PartyPopper, RefreshCw, Target, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../utils/api';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

function GoalComplete() {
  var navigate = useNavigate();
  var [resetting, setResetting] = useState(false);
  var [showConfetti, setShowConfetti] = useState(true);

  // Generate confetti particles
  var confettiParticles = [];
  for (var i = 0; i < 50; i++) {
    confettiParticles.push({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      color: ['#14D5F6', '#FFD700', '#FF6B6B', '#4ADE80', '#A855F7'][Math.floor(Math.random() * 5)]
    });
  }

  // Reset goal and start new plan
  function handleStartNewGoal() {
    setResetting(true);
    
    // Navigate to onboarding to set new goal
    setTimeout(function() {
      navigate('/onboarding/goal');
    }, 500);
  }

  // Continue with current settings
  function handleContinue() {
    setResetting(true);
    
    profileAPI.updatePlanning({ week_number: 1 })
      .then(function() {
        toast.success('Plan reset! Starting fresh week 1.');
        navigate('/home');
      })
      .catch(function(err) {
        console.error('[GoalComplete] Reset error:', err);
        toast.error('Failed to reset plan');
        setResetting(false);
      });
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background relative overflow-hidden">
      {/* Confetti Animation */}
      <AnimatePresence>
        {showConfetti && (
          <>
            {confettiParticles.map(function(particle) {
              return (
                <motion.div
                  key={particle.id}
                  className="absolute w-2 h-2 rounded-full"
                  style={{ 
                    left: particle.x + '%', 
                    backgroundColor: particle.color,
                    top: -20
                  }}
                  initial={{ y: -20, opacity: 1, rotate: 0 }}
                  animate={{ 
                    y: '100vh', 
                    opacity: 0,
                    rotate: 360
                  }}
                  transition={{ 
                    duration: particle.duration, 
                    delay: particle.delay,
                    ease: 'linear'
                  }}
                />
              );
            })}
          </>
        )}
      </AnimatePresence>

      <div className="header-safe-top px-6 pb-6 space-y-8 relative z-10">
        {/* Celebration Header */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="text-center pt-8"
        >
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-accent to-accent/60 flex items-center justify-center shadow-lg shadow-accent/30">
            <Trophy size={48} className="text-white" />
          </div>
          
          <motion.h1
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-heading font-bold text-3xl mb-2"
          >
            Goal Complete! 🎉
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-muted-foreground"
          >
            You&apos;ve completed your nutrition plan!
          </motion.p>
        </motion.div>

        {/* Achievement Stats block intentionally removed: it previously
            showed hardcoded fake numbers (12 weeks / 84 days / 168K
            calories / 12.6K protein) identically to every user, presented
            as if it were their real history. Re-add only once backed by a
            real aggregate query (see AGENTS.md pre-launch audit, finding
            PROG-1) — do not reintroduce placeholder numbers. */}

        {/* What's Next Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-4"
        >
          <h2 className="font-heading font-semibold">What&apos;s Next?</h2>
          
          <button
            onClick={handleStartNewGoal}
            disabled={resetting}
            className="w-full p-4 rounded-xl bg-accent text-white flex items-center justify-between hover:bg-accent/90 transition-colors disabled:opacity-50"
            data-testid="new-goal-btn"
          >
            <div className="flex items-center gap-3">
              <Target size={24} />
              <div className="text-left">
                <p className="font-semibold">Set a New Goal</p>
                <p className="text-sm opacity-80">Recalculate your macros for a new objective</p>
              </div>
            </div>
            <ChevronRight size={20} />
          </button>
          
          <button
            onClick={handleContinue}
            disabled={resetting}
            className="w-full p-4 rounded-xl bg-secondary flex items-center justify-between hover:bg-secondary/80 transition-colors disabled:opacity-50"
            data-testid="continue-btn"
          >
            <div className="flex items-center gap-3">
              <RefreshCw size={24} className="text-muted-foreground" />
              <div className="text-left">
                <p className="font-semibold">Continue Current Plan</p>
                <p className="text-sm text-muted-foreground">Reset to week 1 with same macros</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-muted-foreground" />
          </button>
        </motion.div>

        {/* Motivational Quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center py-6"
        >
          <p className="text-muted-foreground italic">
            &ldquo;Success is the sum of small efforts repeated day in and day out.&rdquo;
          </p>
          <p className="text-sm text-muted-foreground mt-2">— Robert Collier</p>
        </motion.div>
      </div>

      <BottomNav />
    </div>
  );
}

export default GoalComplete;
